import { isEmpty } from 'lodash';
import { Dispatch } from 'redux';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { getChildren, patchPersonInfo, postChild, postDomesticPartner, getDomesticPartners, putDomesticPartner, putChild } from '../../../../global/ducks/person-prc-api';
import { requiredDocUploadFieldChanged } from '../../../../global/helpers/doc-upload-helpers';
import { appendDocument, formatDate, getCurrentFormattedDateNY, parseChanged, parsePhone } from '../../../../global/helpers/form-data-helper';
import { ApiStatuses } from '../../../../global/models/api-model';
import { DemographicAppKeys } from '../../../../global/models/demographics-model';
import { Person } from '../../../../global/models/person-model';
import { DocumentUploadState } from '../personal-info/document-upload-models';
import { processRequestWithDocumentUpload } from '../personal-info/document-upload-request';
import { setDocumentUploadStatus, toggleDocumentUpload } from '../personal-info/document-upload-slice';
import { DependentFormValues } from './dependent-form-models';
import { ChildTypes, DependentTypes } from './dependents-model';
import { dispatchSetChildren, dispatchSetChildrenFailed, setPostDependentStatus, updateDependentPerson, dispatchSetDomesticPartners, dispatchSetDomesticPartnersFailed, setPatchDependentStatus } from './dependents-slice';
import { PersonalInfoPersonTypes } from '../personal-info/personal-info-models';
import { completePersonType } from '../../helpers/personal-info-helpers';
import { clearBeneficiaries, clearBeneficiariesRelationships } from '../../../arc/features/beneficiary/beneficiary-slices';
import { clearMemberNewHireEnrollmentCoverage, clearNewHireEnrollmentCoverage } from '../../../arc/features/new-hire-enrollment/slice/new-hire-enrollment-slices';
import { createEnrollmentWindow } from '../mycpg-midyear/helpers/mycpg-midyear-coverage-form-helpers';
import { QualifyingEventType } from '../mycpg-midyear/models/mycpg-mid-year-models';
import { MyCpgMidYearPersonalInfoAlertTypes } from '../../../notification/notification-models';
import { FormTypes } from '../../models/form-models';


function mapChildTypeToQualifyingEventType(childTypes: string): QualifyingEventType {
	switch (childTypes) {
		case ChildTypes.step:
			return QualifyingEventType.step_child;

		case ChildTypes.adopted:
			return QualifyingEventType.adopted_child;

		case ChildTypes.legal:
		default:
			return QualifyingEventType.legal_child;
	}
}

export const handleSubmitChildPost = (
	app: string,
	dispatch: Dispatch<any>,
	history: any,
	guid: string,
	initialValues: DependentFormValues,
	values: DependentFormValues,
	docUploadState: DocumentUploadState,
	myCpgMidYearPath: string,
	relationshipPath: string,
	formSuccessAction?: () => void) => {
	dispatch(setPostDependentStatus(ApiStatuses.None, app));
	dispatch(apiErrorClear());

	const ignoreKeys = new Set(['child_type', 'disabled_child_indicator']);

	const withPhoneUpdate = parsePhone(values) as DependentFormValues;
	const toUpdate = parseChanged(initialValues, withPhoneUpdate, true, ignoreKeys);
	const startDate = values.start_date ? formatDate(values.start_date) : formatDate(getCurrentFormattedDateNY());
	const body = {
		...toUpdate,
		date_of_birth: formatDate(values.date_of_birth),
		start_date: startDate,
		isMatchPerson: false,
	};

	// The adopted child will eventually be a legal child
	if (body.child_type === ChildTypes.adopted) {
		body.adoption_date_child = startDate;
		body.child_type = ChildTypes.legal;
	}

	delete body.dependent_type;
	delete body.attestation1;
	delete body.attestation2;

	const postChildSuccess = () => {
		dispatch(getChildren(guid, dispatchSetChildren(dispatch, app), () => dispatchSetChildrenFailed(dispatch, app)));
		if (formSuccessAction) formSuccessAction();
		if (app === DemographicAppKeys.myCPG) {
			createEnrollmentWindow(
				guid,
				{
					enrollment_window: {
						qualifying_event: mapChildTypeToQualifyingEventType(values?.child_type),
						qualifying_event_date: values?.child_type === ChildTypes.legal ? formatDate(values.date_of_birth) : formatDate(values?.start_date),
					},
				},
				() => history.push(`${myCpgMidYearPath}?alerts=${MyCpgMidYearPersonalInfoAlertTypes.CHILD_ADD}`),
				(res) => history.push(`${relationshipPath}?success=${FormTypes.POST_DEPENDENT},${res?.response?.status === 422 ? FormTypes.IN_PROGRESS_MID_YEAR_WARNING : ''}`),
				dispatch,
			);
		} else {
			history.push(`${relationshipPath}?success=${FormTypes.MAP_POST_DEPENDENT}`);
		}
	};

	const postRequest = (payload: any) => {
		dispatch(postChild(guid, payload, () => {
			postChildSuccess();
		}));
	};

	const onDocUploadSuccess = (changes: any) => (data) => {
		const withDocuments = appendDocument(changes, data?.map(a => a?.data?.doc_id));
		postRequest(withDocuments);
	};

	// if docs to upload, upload them, then do the patch with the doc information
	if (docUploadState.stagedDocuments && docUploadState.stagedDocuments.length > 0) {
		processRequestWithDocumentUpload(docUploadState, guid, dispatch, onDocUploadSuccess(body));
	} else {
		postRequest(body);
	}
};

export const handleSubmitDomesticPartnerPost = (
	app: string,
	dispatch: Dispatch<any>,
	guid: string,
	initialValues: DependentFormValues,
	values: DependentFormValues,
	docUploadState: DocumentUploadState,
	formSuccessAction?: () => void) => {
	dispatch(setPostDependentStatus(ApiStatuses.None, app));
	dispatch(apiErrorClear());

	const withPhoneUpdate = parsePhone(values) as DependentFormValues;
	const toUpdate = parseChanged(initialValues, withPhoneUpdate, true);

	const body = {
		...toUpdate,
		date_of_birth: formatDate(values.date_of_birth),
		start_date: formatDate(values.start_date),
		isMatchPerson: false,
	};
	delete body.dependent_type;
	delete body.child_type;

	const postDomesticPartnerSuccess = () => {
		dispatch(getDomesticPartners(guid, dispatchSetDomesticPartners(dispatch, app), () => dispatchSetDomesticPartnersFailed(dispatch, app)));
		dispatch(setPostDependentStatus(ApiStatuses.Success, app));
		if (formSuccessAction) formSuccessAction();
	};

	const postRequest = (payload) => {
		dispatch(postDomesticPartner(guid, payload, () => {
			postDomesticPartnerSuccess();
		}));
	};

	const onDocUploadSuccess = (changes: any) => (data) => {
		const withDocuments = appendDocument(changes, data?.map(a => a?.data?.doc_id));
		postRequest(withDocuments);
	};

	// if docs to upload, upload them, then do the patch with the doc information
	if (docUploadState.stagedDocuments && docUploadState.stagedDocuments.length > 0) {
		processRequestWithDocumentUpload(docUploadState, guid, dispatch, onDocUploadSuccess(body));
	} else {
		postRequest(body);
	}

};

export const handleSubmitPut = (app: DemographicAppKeys, dispatch: Dispatch<any>, guid: string, searchGuid: string, formValues: DependentFormValues, docUploadState: DocumentUploadState) => {
	dispatch(setPostDependentStatus(ApiStatuses.None, app));
	dispatch(apiErrorClear());

	const body: any = {
		start_date: formatDate(formValues.start_date),
		guid: searchGuid,
	};

	const domesticPartner = formValues.dependent_type === DependentTypes.domesticPartner;

	if (!domesticPartner) {
		body.child_type = formValues.child_type;
	}

	const putRequest = (payload: any) => {
		if (domesticPartner) {
			dispatch(putDomesticPartner(guid, payload, () => {
				// success callback
				dispatch(getDomesticPartners(guid, dispatchSetDomesticPartners(dispatch, app), () => dispatchSetDomesticPartnersFailed(dispatch, app)));
				dispatch(setPostDependentStatus(ApiStatuses.Success, app));
			}));
		} else {
			dispatch(putChild(guid, payload, () => {
				// success callback
				dispatch(getChildren(guid, dispatchSetChildren(dispatch, app), () => dispatchSetChildrenFailed(dispatch, app)));
				dispatch(setPostDependentStatus(ApiStatuses.Success, app));
			}));
		}
	};

	const onDocUploadSuccess = (changes: any) => (data) => {
		const withDocuments = appendDocument(changes, data?.map(a => a?.data?.doc_id));
		putRequest(withDocuments);
	};

	// if docs to upload, upload them, then do the patch with the doc information
	if (docUploadState.stagedDocuments && docUploadState.stagedDocuments.length > 0) {
		processRequestWithDocumentUpload(docUploadState, guid, dispatch, onDocUploadSuccess(body));
	} else {
		putRequest(body);
	}
};


export const handleDependentChange = (
	app: DemographicAppKeys,
	dispatch: Dispatch<any>,
	dependentPersonGuid: string,
	savedValues: DependentFormValues,
	formValues: DependentFormValues,
	docUploadState: DocumentUploadState,
	memberGuid: string,
	personType: PersonalInfoPersonTypes,
	skipDocUpload: boolean,
) => {

	const withPhoneUpdate = parsePhone(formValues) as DependentFormValues;
	const toUpdate = parseChanged(savedValues, withPhoneUpdate, true);

	completePersonType(toUpdate, memberGuid, personType);

	if (!docUploadState?.documentUploaded && requiredDocUploadFieldChanged(toUpdate, savedValues, skipDocUpload)) {
		dispatch(toggleDocumentUpload(true, app));
		return;
	}

	const { ...dependentInfo } = toUpdate;

	const hasDependentInfoChanges = !isEmpty({ ...dependentInfo });


	const patchPersonSuccess = (person: Person) => {
		if (formValues.dependent_type === DependentTypes.child) {
			dispatch(updateDependentPerson(person, DependentTypes.child, app));
		} else if (formValues.dependent_type === DependentTypes.domesticPartner) {
			dispatch(updateDependentPerson(person, DependentTypes.domesticPartner, app));
		}
		dispatch(setPatchDependentStatus(ApiStatuses.Success, app));
		dispatch(setDocumentUploadStatus(false, app));

		dispatch(clearBeneficiaries());
		dispatch(clearBeneficiariesRelationships());

		dispatch(clearMemberNewHireEnrollmentCoverage());
		dispatch(clearNewHireEnrollmentCoverage());
	};


	const patchRequest = (payload: any) => {
		dispatch(patchPersonInfo(dependentPersonGuid,
			payload,
			skipDocUpload,
			(patchPersonResponse) => {
				patchPersonSuccess(patchPersonResponse.data);
			}));
	};

	const onDocUploadSuccess = (changes: any) => (data) => {
		const withDocuments = appendDocument(changes, data?.map(a => a?.data?.doc_id));
		patchRequest(withDocuments);
	};


	if (hasDependentInfoChanges) {
		dispatch(setPatchDependentStatus(ApiStatuses.None, app));
		dispatch(apiErrorClear());

		// if docs to upload, upload them, then do the patch with the doc information
		if (docUploadState.stagedDocuments && docUploadState.stagedDocuments.length > 0 && docUploadState.showDocumentUpload) {
			processRequestWithDocumentUpload(docUploadState, dependentPersonGuid, dispatch, onDocUploadSuccess(toUpdate));
		} else {
			patchRequest({ ...dependentInfo });
		}
	}
};